<template>
    <div class="pc-page">
        <!-- Header -->
        <div class="pc-header">
            <div class="pc-header-left">
                <div class="pc-icon-wrap"><i class="fas fa-folder-tree"></i></div>
                <h2 class="pc-title">Project Categories</h2>
                <span class="pc-count" v-if="items.length">{{ items.length }}</span>
            </div>
            <AllowPermission :permission="PERMISSION_CODES.EDIT_PROJECT_CATEGORIES">
                <button class="pc-add-btn" @click="showForm = true">
                    <i class="fas fa-plus"></i>
                    Add Category
                </button>
            </AllowPermission>
        </div>

        <!-- Form Modal -->
        <RightSideModal :show="showForm" :title="editMode ? 'Edit Category' : 'New Category'" @close="closeForm">
            <Form :fields="createFields" title="Project Category" submitUrl="/project-categories"
                updateRootUrl="/project-categories" @success="handleFormSuccess" @cancel="closeForm"
                :editMode="editMode" :editData="editData" />
        </RightSideModal>

        <!-- Loading -->
        <div v-if="loading" class="pc-loading"><div class="pc-spinner"></div><span>Loading categories...</span></div>

        <!-- Empty State -->
        <div v-else-if="!items.length" class="pc-empty">
            <div class="pc-empty-icon"><i class="fas fa-folder-tree"></i></div>
            <h3>No project categories found</h3>
            <p>Create your first category to get started</p>
        </div>

        <!-- Cards Grid -->
        <div v-else class="pc-grid">
            <div class="pc-card" v-for="(item, idx) in items" :key="item.id" :style="{ animationDelay: `${idx * 0.03}s` }">
                <div class="pc-card-top">
                    <div class="pc-card-icon" :class="pcColor(idx)"><i class="fas fa-folder"></i></div>
                    <div class="pc-card-info">
                        <h4 class="pc-card-name">{{ item.name }}</h4>
                    </div>
                </div>
                <div class="pc-card-actions">
                    <AllowPermission :permission="PERMISSION_CODES.EDIT_PROJECT_CATEGORIES">
                        <button class="pc-action-btn pc-btn-edit" @click="enableEditing(item)" title="Edit">
                            <i class="fas fa-pen"></i> Edit
                        </button>
                    </AllowPermission>
                    <AllowPermission :permission="PERMISSION_CODES.EDIT_PROJECT_CATEGORIES">
                        <button class="pc-action-btn pc-btn-delete" @click="deleteItem(item.id)" title="Delete">
                            <i class="fas fa-trash-alt"></i> Delete
                        </button>
                    </AllowPermission>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Form from "../../components/crud/form.vue"
import RightSideModal from "../../components/layout/RightSideModal.vue"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import { PERMISSION_CODES } from "../../store/permission"
import { createField } from "../../utils/common"
import { http } from "../../utils/http"

export default {
    data() {
        return {
            PERMISSION_CODES,
            createFields: [
                createField("Name*", "name", "text", true),
            ],
            items: [],
            loading: true,
            showForm: false,
            editMode: false,
            editData: null,
        }
    },
    components: { Form, RightSideModal, AllowPermission },
    mounted() { this.fetchData() },
    methods: {
        pcColor(i) { return ["pc-clr-blue","pc-clr-purple","pc-clr-teal","pc-clr-amber","pc-clr-rose"][i % 5] },
        closeForm() { this.showForm = false; this.editMode = false; this.editData = null },
        handleFormSuccess() { this.closeForm(); this.fetchData() },
        enableEditing(item) { this.editMode = true; this.editData = { ...item }; this.showForm = true; window.scrollTo({ top: 0, behavior: "smooth" }) },
        async fetchData() {
            this.loading = true
            try { const res = await http.get("/project-categories"); this.items = res.data.result || [] } catch (err) { console.log(err) }
            this.loading = false
        },
        async deleteItem(id) {
            if (!confirm("Delete this category?")) return
            try { await http.delete(`/project-categories/${id}`); this.$toast.success("Deleted"); this.items = this.items.filter(c => c.id !== id) }
            catch (e) { this.$toast.error("Error deleting") }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.pc-page { animation: pcFade .3s ease; }
@keyframes pcFade { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }

/* Header */
.pc-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    background: white;
    border: 1px solid $gray100;
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 16px;
    box-shadow: $shadowSm;
    flex-wrap: wrap;
}

.pc-header-left {
    display: flex;
    align-items: center;
    gap: 10px;
}

.pc-icon-wrap {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: linear-gradient(135deg, $primaryLight, darken($primaryLight, 4%));
    color: $primaryColor;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
}

.pc-title {
    font-size: 15px;
    font-weight: 700;
    color: $gray800;
    margin: 0;
}

.pc-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: $radiusFull;
    font-size: 11px;
    font-weight: 700;
    background: $primaryColor;
    color: white;
}

.pc-add-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    border: none;
    border-radius: 6px;
    background: $primaryColor;
    color: white;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    transition: all $transitionFast;
    font-family: inherit;

    &:hover {
        background: $primaryDark;
    }
}

/* Loading / Empty */
.pc-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 80px 16px;
    background: white;
    border: 1px solid $gray100;
    border-radius: 8px;
    box-shadow: $shadowSm;

    span {
        font-size: 13px;
        color: $gray400;
    }
}

.pc-spinner {
    width: 24px;
    height: 24px;
    border: 2.5px solid $gray200;
    border-top-color: $primaryColor;
    border-radius: 50%;
    animation: pcSpin .7s linear infinite;
}

@keyframes pcSpin { to { transform: rotate(360deg); } }

.pc-empty {
    text-align: center;
    padding: 80px 16px;
    background: white;
    border: 1px solid $gray100;
    border-radius: 8px;
    box-shadow: $shadowSm;

    h3 {
        font-size: 14px;
        font-weight: 700;
        color: $gray600;
        margin: 0 0 4px;
    }

    p {
        font-size: 12px;
        color: $gray400;
        margin: 0;
    }
}

.pc-empty-icon {
    width: 48px;
    height: 48px;
    border-radius: $radiusFull;
    background: $gray50;
    border: 1px solid $gray100;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 12px;

    i {
        font-size: 20px;
        color: $gray300;
    }
}

/* Grid */
.pc-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 12px;
}

.pc-card {
    background: white;
    border: 1px solid $gray100;
    border-radius: 10px;
    padding: 16px;
    box-shadow: $shadowSm;
    display: flex;
    flex-direction: column;
    gap: 12px;
    animation: pcSlide .25s ease both;
    transition: all $transitionFast;

    &:hover {
        border-color: $gray200;
        box-shadow: $shadowMd;
        transform: translateY(-2px);
    }
}

@keyframes pcSlide { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }

.pc-card-top {
    display: flex;
    align-items: center;
    gap: 12px;
}

.pc-card-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    flex-shrink: 0;
}

.pc-clr-blue { background: #eff6ff; color: #2563eb; }
.pc-clr-purple { background: #f5f3ff; color: #7c3aed; }
.pc-clr-teal { background: #f0fdfa; color: #0d9488; }
.pc-clr-amber { background: #fffbeb; color: #d97706; }
.pc-clr-rose { background: #fff1f2; color: #e11d48; }

.pc-card-info {
    flex: 1;
}

.pc-card-name {
    font-size: 14px;
    font-weight: 700;
    color: $gray800;
    margin: 0;
    line-height: 1.3;
}

/* Actions */
.pc-card-actions {
    display: flex;
    gap: 6px;
    padding-top: 10px;
    border-top: 1px solid $gray100;
}

.pc-action-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    border: 1px solid $gray200;
    border-radius: 5px;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    transition: all $transitionFast;
    font-family: inherit;
    background: white;
    flex: 1;
    justify-content: center;

    i {
        font-size: 10px;
    }
}

.pc-btn-edit {
    color: $primaryColor;

    &:hover {
        background: $primaryLight;
        border-color: rgba(0, 94, 137, 0.15);
    }
}

.pc-btn-delete {
    color: $danger;

    &:hover {
        background: #fef2f2;
        border-color: rgba(220, 38, 38, 0.15);
    }
}

/* Responsive */
@media (max-width: 992px) {
    .pc-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 480px) {
    .pc-grid {
        grid-template-columns: 1fr;
    }

    .pc-card {
        padding: 12px;
    }

    .pc-header {
        flex-direction: column;
        align-items: stretch;
    }
}
</style>
